// v2 dolphin — a bottlenose with a real porpoising profile: a fusiform body
// arched over the back, a pale belly panel, a rounded melon flowing into a
// slender rostrum, a sickle dorsal fin (main blade plus a recurved tip),
// splayed pectoral flippers, a tapering tail stock and broad horizontal flukes,
// plus a blowhole dot. Two skins: v0 grey bottlenose, v1 slate common dolphin
// with a tan flank blaze. Ported 1:1 from age-of-mcp's v2/dolphin.ts, using the
// library's shared `wetland` (eyes) helper.
import * as THREE from "three";
import { defineModel, wetland, type Part } from "../../lib";

const SKINS = [
  { back: "#6e8492", body: "#8298a6", belly: "#c2ccd4", fin: "#5c7280",
    blaze: null as string | null },
  { back: "#4a5e6e", body: "#68808f", belly: "#d2d8dc", fin: "#3e525e",
    blaze: "#d8b878" },
];

function dolphinParts(v: number): Part[] {
  const c = SKINS[v % SKINS.length]!;
  const parts: Part[] = [
    // fusiform body with the arched back
    { geometry: new THREE.IcosahedronGeometry(0.052, 0), color: c.body,
      position: [0.005, 0.042, 0], scale: [2.0, 0.85, 0.7] },
    { geometry: new THREE.IcosahedronGeometry(0.04, 0), color: c.back,
      position: [-0.01, 0.054, 0], scale: [1.5, 0.75, 0.6] },
    { geometry: new THREE.IcosahedronGeometry(0.038, 0), color: c.belly,
      position: [0.015, 0.03, 0], scale: [1.85, 0.65, 0.6] },
    // melon and slender rostrum
    { geometry: new THREE.IcosahedronGeometry(0.024, 0), color: c.body,
      position: [0.085, 0.048, 0], scale: [1.1, 0.9, 0.78] },
    { geometry: new THREE.ConeGeometry(0.0105, 0.034, 5), color: c.belly,
      position: [0.118, 0.042, 0], rotation: [0, 0, -Math.PI / 2],
      scale: [0.75, 1, 0.8] },
    // sickle dorsal — swept-back blade with a recurved tip
    { geometry: new THREE.ConeGeometry(0.014, 0.05, 3), color: c.fin,
      position: [-0.01, 0.092, 0], rotation: [0, 0, 0.7], scale: [1, 1, 0.28] },
    { geometry: new THREE.ConeGeometry(0.006, 0.022, 3), color: c.fin,
      position: [-0.032, 0.108, 0], rotation: [0, 0, 1.15], scale: [1, 1, 0.28] },
    // tail stock tapering into broad horizontal flukes
    { geometry: new THREE.CylinderGeometry(0.008, 0.017, 0.05, 5), color: c.body,
      position: [-0.115, 0.046, 0], rotation: [0, 0, Math.PI / 2 + 0.15] },
    { geometry: new THREE.ConeGeometry(0.011, 0.034, 3), color: c.fin,
      position: [-0.148, 0.052, 0.011], rotation: [0, 0.55, Math.PI / 2 - 0.1],
      scale: [0.22, 1, 0.8] },
    { geometry: new THREE.ConeGeometry(0.011, 0.034, 3), color: c.fin,
      position: [-0.148, 0.052, -0.011], rotation: [0, -0.55, Math.PI / 2 - 0.1],
      scale: [0.22, 1, 0.8] },
    // pectoral flippers swept back-down-out
    { geometry: new THREE.ConeGeometry(0.008, 0.026, 3), color: c.fin,
      position: [0.045, 0.026, 0.022], rotation: [0, 0.6, 2.3], scale: [0.3, 1, 0.9] },
    { geometry: new THREE.ConeGeometry(0.008, 0.026, 3), color: c.fin,
      position: [0.045, 0.026, -0.022], rotation: [0, -0.6, 2.3], scale: [0.3, 1, 0.9] },
    // blowhole
    { geometry: new THREE.IcosahedronGeometry(0.0035, 0), color: "#2a3438",
      position: [0.06, 0.082, 0] },
    ...wetland.eyes(0.095, 0.05, 0.0165, 0.0035),
  ];
  if (c.blaze) {
    // tan hourglass blaze along each flank (common dolphin)
    parts.push(
      { geometry: new THREE.IcosahedronGeometry(0.022, 0), color: c.blaze,
        position: [0.045, 0.038, 0.026], scale: [1.6, 0.45, 0.3] },
      { geometry: new THREE.IcosahedronGeometry(0.022, 0), color: c.blaze,
        position: [0.045, 0.038, -0.026], scale: [1.6, 0.45, 0.3] },
    );
  }
  return parts;
}

export const dolphinV2 = defineModel({
  id: "dolphin-v2",
  scale: 2.18, // real-world size correction (see Model.scale)
  batchId: "fable",
  name: "Dolphin (v2)",
  tags: ["creature","dolphin","low-poly","age-of-mcp","fable","water"],
  summary: "Low-poly dolphin.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.12,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => dolphinParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => dolphinParts(1) },
  ],
  animations: [],
});
